import { TFunction } from "i18next";

import {
  SimCardDetail,
  SimCardDetailSimStatus,
  SimCardDetailSimStatusReason,
} from "@/services/model";
import {
  getTariffType,
  hasNumberPorting,
  TariffTypes,
} from "@/utils/deviceUtils";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

/**
 * State derived purely from the currently displayed SIM. These flags describe
 * what the SIM *is* (its status and tariff), independent of what the user is
 * currently doing in the UI.
 */
export interface DeviceSimState {
  /** The SIM is switched off (grey status). */
  isInactiveSim: boolean;
  /** A number porting is still in progress for this SIM. */
  isNumberPorting: boolean;
  /**
   * There is no subscription that can be managed right now — the SIM is
   * inactive, porting, has a pending order, or is blocked after a failed
   * payment.
   */
  hasNoUsableSubscription: boolean;
  /** The service id that describes the SIM's (current or upcoming) tariff. */
  tariffServiceId: number | undefined;
  /** The tariff is a Flat Home product. */
  isFlatHome: boolean;
  /** The tariff carries data only, i.e. no telephony. */
  isDataOnly: boolean;
}

/**
 * Reduces a {@link SimCardDetail} to the handful of booleans the tab strip and
 * its hints depend on. Kept free of React so the branching can be unit-tested
 * in isolation.
 */
export const deriveSimState = (simData: SimCardDetail): DeviceSimState => {
  const isInactiveSim = simData.simStatus === SimCardDetailSimStatus.grey;
  const isNumberPorting = hasNumberPorting(simData);
  // Call settings belong to a running subscription: the design greys the tab
  // out while the SIM has none, is still being prepared, or is blocked after a
  // failed payment.
  const hasNoUsableSubscription =
    isInactiveSim ||
    isNumberPorting ||
    !!simData.servicePending ||
    simData.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED;

  // A SIM whose package has lapsed still knows what it renews into, and the
  // tab strip describes that product — the same fallback the Abo tab uses.
  const tariffServiceId = simData.serviceID ?? simData.successorForRenew?.id;
  const isFlatHome = isFlatHomeTariff(tariffServiceId);
  // A data-only subscription carries no telephony, so the frames drop the tab
  // rather than showing it blocked ("Tab [Anrufe]" → section "FlatDATA").
  // `getTariffType` answers dataTariff for everything it does not know,
  // undefined included, so an unread serviceID must not reach it — otherwise
  // the tab disappears while the SIM is still loading and for every tariff the
  // catalogue has not been taught yet.
  const isDataOnly =
    tariffServiceId !== undefined &&
    getTariffType(tariffServiceId) === TariffTypes.dataTariff;

  return {
    isInactiveSim,
    isNumberPorting,
    hasNoUsableSubscription,
    tariffServiceId,
    isFlatHome,
    isDataOnly,
  };
};

/**
 * State describing what the user is currently doing — which sub-flow is open
 * (booking, checkout, editing, …). Every field is already reduced to a plain
 * boolean so the disable rules below read as pure OR-chains.
 */
export interface DeviceFlowState {
  isBookingWatchSIM: boolean;
  isChangingVoiceSettings: boolean;
  isChangingTariff: boolean;
  isModifyingESim: boolean;
  isEditingSimDetails: boolean;
  isSimDetailsSectionActive: boolean;
  isModifyingWatchSim: boolean;
  isBookingRoaming: boolean;
}

/** Whether each management tab is disabled for the current flow and SIM. */
export interface TabDisabledState {
  isRoamingDisabled: boolean;
  isTariffDisabled: boolean;
  isVoiceDisabled: boolean;
  isWatchSimTabDisabled: boolean;
  isDetailsDisabled: boolean;
}

/**
 * Computes which tabs must be disabled. A tab is blocked whenever an unrelated
 * flow is open (so the user cannot jump away mid-checkout) or the SIM cannot
 * support the tab's action. Each rule is a flat OR-chain over the flow and SIM
 * flags, mirroring the frames one-to-one.
 */
export const computeTabDisabledState = (
  flow: DeviceFlowState,
  sim: Pick<
    DeviceSimState,
    "isNumberPorting" | "isInactiveSim" | "hasNoUsableSubscription"
  >,
): TabDisabledState => ({
  isRoamingDisabled:
    flow.isBookingWatchSIM ||
    flow.isChangingVoiceSettings ||
    flow.isChangingTariff ||
    flow.isModifyingESim ||
    flow.isEditingSimDetails ||
    flow.isSimDetailsSectionActive ||
    sim.isNumberPorting ||
    flow.isModifyingWatchSim,
  isTariffDisabled:
    flow.isBookingWatchSIM ||
    flow.isChangingVoiceSettings ||
    flow.isBookingRoaming ||
    flow.isModifyingESim ||
    flow.isEditingSimDetails ||
    flow.isSimDetailsSectionActive ||
    flow.isModifyingWatchSim ||
    sim.isNumberPorting,
  isVoiceDisabled:
    flow.isBookingWatchSIM ||
    flow.isBookingRoaming ||
    sim.hasNoUsableSubscription ||
    flow.isChangingTariff ||
    flow.isModifyingESim ||
    flow.isEditingSimDetails ||
    flow.isSimDetailsSectionActive ||
    flow.isModifyingWatchSim,
  isWatchSimTabDisabled:
    flow.isChangingVoiceSettings ||
    flow.isBookingRoaming ||
    sim.isInactiveSim ||
    flow.isChangingTariff ||
    flow.isModifyingESim ||
    flow.isEditingSimDetails ||
    flow.isSimDetailsSectionActive,
  isDetailsDisabled:
    flow.isBookingWatchSIM ||
    flow.isChangingVoiceSettings ||
    flow.isBookingRoaming ||
    flow.isChangingTariff ||
    flow.isModifyingWatchSim,
});

/** The hints shown on disabled tabs, explaining why they cannot be opened. */
export interface DeviceTabHints {
  inactiveSimHint?: string;
  numberPortingHint?: string;
  /** Hint for the Voice tab, picking the most specific blocking reason. */
  noSubscriptionHint?: string;
}

/**
 * Builds the disabled-tab hints. A disabled tab explains itself; without a hint
 * it is just greyed out. For the Voice tab the most specific reason wins: a
 * blocked payment explains more than "no active subscription", which in turn
 * explains more than a pending order.
 */
export const computeDisabledHints = (
  simData: SimCardDetail,
  sim: Pick<DeviceSimState, "isInactiveSim" | "isNumberPorting">,
  t: TFunction,
): DeviceTabHints => {
  const inactiveSimHint = sim.isInactiveSim
    ? t("cockpit.managementTile.tabs.disabledHint.inactive")
    : undefined;
  const numberPortingHint = sim.isNumberPorting
    ? t("cockpit.managementTile.tabs.disabledHint.numberPorting")
    : undefined;
  const servicePendingHint = simData.servicePending
    ? t("cockpit.managementTile.tabs.disabledHint.servicePending")
    : undefined;
  const lockRequestedHint =
    simData.simStatusReason === SimCardDetailSimStatusReason.LOCK_REQUESTED
      ? t("cockpit.managementTile.tabs.disabledHint.lockRequested")
      : undefined;
  // The most specific reason wins: a blocked payment explains more than "no
  // active subscription", which in turn explains more than a pending order.
  const noSubscriptionHint =
    lockRequestedHint ??
    numberPortingHint ??
    inactiveSimHint ??
    servicePendingHint;

  return { inactiveSimHint, numberPortingHint, noSubscriptionHint };
};
